import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UsageBar } from '@umanforge/common';

import { InputNumber } from '../inputs/input_number.component';
import { ChevronDownIcon } from '../icons/chevron-down.icon';


export interface StorageAllocationProps {
  icon: ReactNode;
  title: string;
  maxQuota: number;
  used: number;
  quota: number;
  alertEnabled: boolean;
  alertThreshold: number;
  unit?: string;
  disabled?: boolean;
  onQuotaChange?: ( value: number ) => void;
  onAlertEnabledChange?: ( value: boolean ) => void;
  onAlertThresholdChange?: ( value: number ) => void;
}

export function StorageAllocation( {
  icon,
  title,
  maxQuota,
  used,
  quota,
  alertEnabled,
  alertThreshold,
  disabled = false,
  onQuotaChange,
  onAlertEnabledChange,
  onAlertThresholdChange,
}: StorageAllocationProps ) {
  const { t } = useTranslation();
  const [ collapsed, setCollapsed ] = useState( true );

  return (
    <div className='flex flex-col gap-3 p-4 rounded-lg border border-gray-200 bg-white shadow-sm w-full'>

      <div className='flex items-center gap-2 font-semibold text-gray-800'>
        { icon }
        <span className='flex-1'>{ title }</span>
        <button
          type='button'
          onClick={ () => { setCollapsed( prev => {return !prev;} ); } }
          className='ml-auto flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors'
          aria-label={ collapsed ? t( 'translation:expand' ) : t( 'translation:collapse' ) }
        >
          <ChevronDownIcon className={ `w-3 h-3 fill-current transition-transform duration-200 ${ collapsed ? '' : 'rotate-180' }` } />
        </button>
      </div>

      <UsageBar
        label={ t( 'translation:current_usage' ) }
        used={ used }
        max={ Number( quota ?? 0 ) * 1048576 }
        warningPercent={ alertEnabled ? Math.floor( alertThreshold * 0.9 ) : undefined }
        dangerPercent={ alertEnabled ? alertThreshold : undefined }
      />

      <div className={ `flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out ${ collapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100' }` }>
        <div className='flex flex-col gap-3 px-px py-px'>
          <div className='flex items-end gap-2 w-full'>
            <div className='flex-1'>
              <InputNumber
                label={ t( 'translation:assigned_quota' ) }
                value={ Number( ( quota ?? 0 ).toFixed( 3 ) ) }
                decimalPlaces={ 2 }
                min={ 0.000001 }
                max={ Number.isFinite( maxQuota ) ? ( maxQuota / 1048576 ) : undefined }
                disabled={ disabled }
                onChange={ value => {
                  if ( onQuotaChange ) {
                    onQuotaChange( Number( value ?? 0 ) );
                  }
                } }
              />
            </div>
            <span className='h-9 flex flex-col items-end text-sm text-gray-600 pb-[1px]'>
              <span className='text-sm text-gray-700'> { t( 'common:file_size.file_size_gb' ) } </span>
            </span>
          </div>

          <div className='flex items-center gap-3'>
            <button
              type='button'
              role='switch'
              aria-checked={ alertEnabled }
              disabled={ disabled }
              onClick={ () => { if ( onAlertEnabledChange ) { onAlertEnabledChange( !alertEnabled ); } } }
              className={
                'relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ' +
                ( alertEnabled ? 'bg-green-500 ' : 'bg-gray-300 ' ) +
                ( disabled ? 'opacity-50 cursor-default' : 'cursor-pointer' )
              }
            >
              <span
                className={
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ' +
                  ( alertEnabled ? 'translate-x-5' : 'translate-x-0' )
                }
              />
            </button>
            <span className='text-sm text-gray-700'>{ t( 'translation:enable_alerts' ) }</span>
          </div>

          <div className='flex items-end gap-2 w-full'>
            <div className='flex-1'>
              <InputNumber
                label={ t( 'translation:alert_threshold' ) }
                value={ parseFloat( alertThreshold.toFixed( 2 ) ) }
                min={ 1 }
                max={ 99 }
                disabled={ disabled || !alertEnabled }
                onChange={ onAlertThresholdChange }
              />
            </div>
            <span className='h-9 flex items-center text-sm text-gray-600 pb-[1px]'>%</span>
          </div>
        </div>
      </div>

    </div>
  );
}
